import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Handler from "../../../Helpers/Handler";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import "../../../Assets/css/style.css";

// const BASE = "http://localhost/business_community/";
const BASE = "https://qnexus.in/community/";

// ── Notification type → icon + color ──────────────
const TYPE_META = {
    DEAL_REQUEST: { icon: "bx-briefcase", color: "#2563eb" },
    DEAL_ACCEPTED: { icon: "bx-check-circle", color: "#16a34a" },
    DEAL_REJECTED: { icon: "bx-x-circle", color: "#dc2626" },
    DEAL_COMPLETED: { icon: "bx-badge-check", color: "#7c3aed" },
    COMMUNITY_INVITE: { icon: "bx-group", color: "#0891b2" },
    community_invite_response: { icon: "bx-group", color: "#16a34a" },
    COMMUNITY_REMOVED: { icon: "bx-user-minus", color: "#dc2626" },
    VENDOR_REVIEW: { icon: "bxs-star", color: "#f59e0b" },
    post_like: { icon: "bxs-heart", color: "#e11d48" },
    post_comment: { icon: "bx-comment", color: "#2563eb" },
};

const getTypeMeta = (type) =>
    TYPE_META[type] || { icon: "bx-bell", color: "#64748b" };

// ── Inline Deal Respond Buttons ────────────────────
const DealRespondButtons = ({ dealId, onDone }) => {
    const [loading, setLoading] = useState(null);
    const [done, setDone] = useState(false);
    const [result, setResult] = useState("");

    const respond = async (status) => {
        setLoading(status);
        const res = await Handler({
            method: "POST",
            url: "/mini_crm/respond.php",
            data: { deal_id: parseInt(dealId), status },
        });
        setLoading(null);
        if (res.success) {
            setDone(true);
            setResult(status);
            onDone && onDone();
        }
    };

    if (done) {
        return (
            <div className={`notif-respond-done ${result === "ACCEPTED" ? "notif-respond-done--accept" : "notif-respond-done--reject"}`}>
                <i className={`bx ${result === "ACCEPTED" ? "bx-check-circle" : "bx-x-circle"}`} />
                Deal {result === "ACCEPTED" ? "Accepted" : "Rejected"}
            </div>
        );
    }

    return (
        <div className="notif-respond-row">
            <button
                className="notif-btn notif-btn--reject"
                onClick={(e) => { e.stopPropagation(); respond("REJECTED"); }}
                disabled={!!loading}
            >
                {loading === "REJECTED"
                    ? <i className="bx bx-loader-alt bx-spin" />
                    : <><i className="bx bx-x" /> Reject</>
                }
            </button>
            <button
                className="notif-btn notif-btn--accept"
                onClick={(e) => { e.stopPropagation(); respond("ACCEPTED"); }}
                disabled={!!loading}
            >
                {loading === "ACCEPTED"
                    ? <i className="bx bx-loader-alt bx-spin" />
                    : <><i className="bx bx-check" /> Accept</>
                }
            </button>
        </div>
    );
};

// ── Inline Community Invite Respond Buttons ────────
const InviteRespondButtons = ({ inviteId, onDone }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null);

    const respond = async (status) => {
        setLoading(status);
        const res = await Handler({
            method: "POST",
            url: "/communities/respond.php",
            data: { invite_id: parseInt(inviteId), status },
        });
        setLoading(null);
        if (res.success) {
            onDone && onDone(status); // ← pass status up, parent handles UI
            if (status === "ACCEPTED") {
                setTimeout(() => navigate("/user/communities"), 1000);
            }
        }
    };

    return (
        <div className="notif-respond-row">
            <button
                className="notif-btn notif-btn--reject"
                onClick={(e) => { e.stopPropagation(); respond("REJECTED"); }}
                disabled={!!loading}
            >
                {loading === "REJECTED"
                    ? <i className="bx bx-loader-alt bx-spin" />
                    : <><i className="bx bx-x" /> Reject</>}
            </button>
            <button
                className="notif-btn notif-btn--accept"
                onClick={(e) => { e.stopPropagation(); respond("ACCEPTED"); }}
                disabled={!!loading}
            >
                {loading === "ACCEPTED"
                    ? <i className="bx bx-loader-alt bx-spin" />
                    : <><i className="bx bx-check" /> Accept</>}
            </button>
        </div>
    );
};


// ── Main Notifications Component ───────────────────
const NotificationsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [list, setList] = useState([]);

    const fetchNotifications = async () => {
        dispatch(loadingTrue());
        try {
            const r = await Handler({ method: "get", url: "/notification/list.php" });
            if (r?.success) setList(r.data.list || []);
        } catch (e) {
            console.log("Notification fetch error", e);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { fetchNotifications(); }, []);

    const markAsRead = async (id) => {
        try {
            await Handler({
                method: "post",
                url: "/notification/mark_read.php",
                data: { notification_id: id },
            });
            setList(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
        } catch (e) { console.log(e); }
    };

    const markAllRead = async () => {
        dispatch(loadingTrue());
        try {
            await Handler({ method: "post", url: "/notification/mark_all_read.php" });
            setList(prev => prev.map(n => ({ ...n, is_read: 1 })));
        } catch (e) { console.log(e); }
        dispatch(loadingFalse());
    };

    return (
        <div className="notifications-wrapper">

            {/* Header */}
            <div className="notifications-header">
                <h4>Notifications</h4>
                {list.some(n => !n.is_read) && (
                    <button className="btn-mark-all" onClick={markAllRead}>
                        Mark all as read
                    </button>
                )}
            </div>

            {list.length === 0 && (
                <div className="no-notifications">No notifications found</div>
            )}

            {list.map(n => {
                const actor = n.from?.profile;
                const logo = actor?.logo
                    ? BASE + actor.logo
                    : "https://ui-avatars.com/api/?name=A";
                const name =
                    n.from?.role === "business"
                        ? actor?.business_name
                        : n.from?.role === "admin"
                            ? "Platform Admin"
                            : "System";

                const { icon, color } = getTypeMeta(n.type);

                return (
                    <div
                        key={n.id}
                        onClick={() => !n.is_read && markAsRead(n.id)}
                        className={`notification-item ${!n.is_read ? "unread" : ""}`}
                    >
                        {/* Avatar + type icon overlay */}
                        <div className="notif-avatar-wrap">
                            <img src={logo} alt="profile" className="notification-avatar" />
                            <span className="notif-type-icon" style={{ background: color }}>
                                <i className={`bx ${icon}`} />
                            </span>
                        </div>

                        <div className="notification-content">
                            <div className="notification-name">{name}</div>
                            <div className="notification-message">
                                <strong>{n?.comment}</strong> {n.message}
                            </div>
                            <span className="notification-time">
                                {moment(n.created_at).fromNow()}
                            </span>

                            {/* ── Post actions ── */}
                            {(n.type === "post_like" || n.type === "post_comment") && n.post_id && (
                                <button
                                    className="btn-view-post"
                                    onClick={(e) => { e.stopPropagation(); navigate(`/user/view-post/${n.post_id}`); }}
                                >
                                    View Post
                                </button>
                            )}

                            {/* ── Deal Request — inline Accept/Reject ── */}
                            {n.type === "DEAL_INVITE" && n.reference_id && (
                                n.deal_status && n.deal_status !== "PENDING"
                                    ? (
                                        <div className={`notif-respond-done ${n.deal_status === "ACCEPTED" ? "notif-respond-done--accept" : "notif-respond-done--reject"}`}>
                                            <i className={`bx ${n.deal_status === "ACCEPTED" ? "bx-check-circle" : "bx-x-circle"}`} />
                                            Deal {n.deal_status === "ACCEPTED" ? "Accepted" : "Rejected"}
                                        </div>
                                    )
                                    : (
                                        <DealRespondButtons
                                            dealId={n.reference_id}
                                            onDone={() => markAsRead(n.id)}
                                        />
                                    )
                            )}

                            {/* ── Deal Accepted — go to chat ── */}
                            {n.type === "DEAL_RESPONSE" && n.reference_id && (
                                <button
                                    className="notif-btn notif-btn--view"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/user/crm/chat/${n.reference_id}`);
                                    }}
                                >
                                    <i className="bx bx-chat" /> Open Chat
                                </button>
                            )}

                            {/* ── Community Invite — inline Accept/Reject ── */}
                            {n.type === "COMMUNITY_INVITE" && (
                                n.invite_status && n.invite_status !== "PENDING"
                                    ? (
                                        <div className={`notif-respond-done ${n.invite_status === "ACCEPTED" ? "notif-respond-done--accept" : "notif-respond-done--reject"}`}>
                                            <i className={`bx ${n.invite_status === "ACCEPTED" ? "bx-check-circle" : "bx-x-circle"}`} />
                                            Invite {n.invite_status === "ACCEPTED" ? "Accepted" : "Rejected"}
                                        </div>
                                    )
                                    : n.invite_id
                                        ? (
                                            <InviteRespondButtons
                                                inviteId={n.invite_id}
                                                onDone={(status) => {
                                                    // ← update list locally so UI reflects instantly
                                                    setList(prev => prev.map(item =>
                                                        item.id === n.id
                                                            ? { ...item, invite_status: status }
                                                            : item
                                                    ));
                                                    markAsRead(n.id);
                                                }}
                                            />
                                        )
                                        : null
                            )}

                            {/* ── Community invite response — view community ── */}
                            {n.type === "community_invite_response" && (
                                <button
                                    className="notif-btn notif-btn--view"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate("/user/communities");
                                    }}
                                >
                                    <i className="bx bx-group" /> View Communities
                                </button>
                            )}

                        </div>

                        {!n.is_read && <div className="unread-indicator" />}
                    </div>
                );
            })}
        </div>
    );
};

export default NotificationsPage;